// utils/supplierReturn.js
//
// The member's post-delivery return door — «أرجّع الصنف» — and the six words
// that report where the goods are afterwards.
//
// BE→FE briefs (2026-09-17): `member-return-request.md` (the door + the six
// status words), `returns-desk-door-and-box.md` and `returns-desk-out-with-
// shop.md` (why a line she never touched can carry the same payload — the
// leader's own «مش زي ما طلب» door writes the identical shape).
// Backend: `app/Enums/Commerce/{MemberReturnStatus,SupplierReturnReason}.php`,
// `OrderBoxItemResource::supplierReturnPayload()`.
//
// THE CONTRACT — `item.supplierReturn`, OMITTED (never null) while the line
// carries no return, or once a return on it was CANCELLED (the backend hides
// the whole payload rather than emit a null status):
//   { status: 'request_received'|'item_received'|'asking_supplier'
//            |'trying_to_resell'|'refunded'|'coming_back',
//     reasonKey: 'damaged'|'wrong_item'|'quality'|'overage'
//               |'customer_refused'|'ordered_by_mistake' }
//
// `reasonKey` is not always one this app's own door can produce — the leader's
// re-check door (returns-desk P1-A) writes the SAME payload shape with reasons
// this door never offers (`overage`), so the reader below handles all six.
//
// Same discipline as utils/supplierSuggestion.js: absence is an omitted key,
// never null; every enum is READ, never assumed, so an unknown value degrades
// to '' rather than reaching a member as a raw key or crashing a screen an
// older-contract app was never built to read.

import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { OrderBoxStatus } from '@/enums/commerce/OrderBoxStatus.enum.js';
import { isTadbeesaLine } from '@/utils/tadbeesa.js';

/** The six words a member's line can carry, in the order she meets them. */
export const MEMBER_RETURN_STATUS = Object.freeze({
  REQUEST_RECEIVED: 'request_received',
  ITEM_RECEIVED: 'item_received',
  ASKING_SUPPLIER: 'asking_supplier',
  TRYING_TO_RESELL: 'trying_to_resell',
  REFUNDED: 'refunded',
  COMING_BACK: 'coming_back',
});

/** Every reason a return LINE may carry, member- or leader-opened alike. */
export const SUPPLIER_RETURN_REASON = Object.freeze({
  WRONG_ITEM: 'wrong_item',
  DAMAGED: 'damaged',
  QUALITY: 'quality',
  OVERAGE: 'overage',
  ORDERED_BY_MISTAKE: 'ordered_by_mistake',
  CUSTOMER_REFUSED: 'customer_refused',
});

/**
 * The five reasons HER OWN post-delivery door may send — `overage` excluded,
 * exactly as the backend's `SupplierReturnReason::postDeliveryCases()`
 * refuses it there (over-delivery is a receiving-time finding, never
 * something a member hands back after the fact). Order mirrors the shared
 * desk sheet's chip row (design §5) so the two never read as two vocabularies.
 */
export const POST_DELIVERY_RETURN_REASONS = Object.freeze([
  SUPPLIER_RETURN_REASON.WRONG_ITEM,
  SUPPLIER_RETURN_REASON.DAMAGED,
  SUPPLIER_RETURN_REASON.QUALITY,
  SUPPLIER_RETURN_REASON.ORDERED_BY_MISTAKE,
  SUPPLIER_RETURN_REASON.CUSTOMER_REFUSED,
]);

const STATUS_VALUES = new Set(Object.values(MEMBER_RETURN_STATUS));
const REASON_VALUES = new Set(Object.values(SUPPLIER_RETURN_REASON));

/** Statuses that mean a return is still going — the door-eligibility rule
 *  reads "no OPEN return" as "absent, or one of the two terminal words". */
const OPEN_STATUSES = new Set([
  MEMBER_RETURN_STATUS.REQUEST_RECEIVED,
  MEMBER_RETURN_STATUS.ITEM_RECEIVED,
  MEMBER_RETURN_STATUS.ASKING_SUPPLIER,
  MEMBER_RETURN_STATUS.TRYING_TO_RESELL,
]);

/** The backend's ceiling on the optional note (`note` → `max:500`). */
export const RETURN_NOTE_MAX = 500;

/**
 * Her line's return, read off the omitted-key contract above — or null when
 * there is nothing to show. An unknown `status` (a future word this shipped
 * app was never taught) degrades to null rather than rendering a raw key or
 * reaching a status test with something neither branch expects; per the
 * brief, that is additive and by design.
 *
 * @param {object|null|undefined} item an OrderBoxItem model or raw resource
 * @returns {{status: string, reasonKey: string|null}|null}
 */
export function readSupplierReturn(item) {
  const raw = item?.supplierReturn;
  if (raw == null || typeof raw !== 'object') return null;

  const status = typeof raw.status === 'string' && STATUS_VALUES.has(raw.status)
    ? raw.status
    : null;
  if (status === null) return null;

  const reasonKey = typeof raw.reasonKey === 'string' && REASON_VALUES.has(raw.reasonKey)
    ? raw.reasonKey
    : null;

  return { status, reasonKey };
}

/**
 * Is there a return on this line the club still owes an answer on? This is
 * the "keep the door shut" half of eligibility — a fresh ask beside "we have
 * your request" would read as though the first one never registered.
 *
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function hasOpenSupplierReturn(item) {
  const supplierReturn = readSupplierReturn(item);
  return supplierReturn !== null && OPEN_STATUSES.has(supplierReturn.status);
}

/**
 * A Poola line, approximated client-side. There is no `isPoola` flag on the
 * wire today — `OrderBoxItemPolicy::requestReturn()` reads `poola_purchase_id`
 * server-side, which this resource never sends — so this reads the one proxy
 * already documented on the model: a Poola line is the only one that ever
 * carries a PoolShare-funded `discount` (OrderBoxItem.js, `knownLineNetCents`).
 * A false negative here is caught by the backend's own 403; a false positive
 * only ever hides a button that was never going to work, which is the safe
 * direction. Worth an explicit flag — see the 2026-09-17 FE→BE follow-up brief.
 *
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function isPoolaLine(item) {
  return Number(item?.discount) > 0;
}

/**
 * Does the carton still accept a return arriving for it? A faithful mirror of
 * the brief's box half of the gate: delivered or closed, AND delivery
 * confirmed — a box merely marked DELIVERED before the confirm step is not
 * enough, exactly as `boxCanTakeItBack()` reads the opposite fact for the
 * suggest-a-shop door beside this one.
 *
 * @param {object|null|undefined} box
 * @returns {boolean}
 */
export function boxAcceptsReturn(box) {
  if (box == null) return false;

  const raw = box.status;
  const status = typeof raw === 'string' ? Number(raw) : raw;

  return (
    (status === OrderBoxStatus.DELIVERED || status === OrderBoxStatus.CLOSED)
    && box.deliveredConfirmedAt != null
  );
}

/**
 * May the member open «أرجّع الصنف» on this line right now? A faithful mirror
 * of `OrderBoxItemPolicy::requestReturn()`: the line itself DELIVERED, its
 * carton still accepting a return, not a Poola line, not a تدبيسة line
 * (2026-09-22 brief — the club's own resale shelf has no return, final sale),
 * and carrying no OPEN return of its own. Rendering the door anywhere else is
 * a guaranteed 403.
 *
 * `tadbeesaChannelId` is the resolved «تدبيسة النادي» channel id
 * (composables/useTadbeesaChannel.js) — omitted while it hasn't resolved yet,
 * which is the safe direction: `isTadbeesaLine` reads a missing id as "not
 * تدبيسة", so the door only ever stays OFFERED a beat too long, never hidden
 * on an ordinary line.
 *
 * @param {object|null|undefined} item
 * @param {{status: string|number, deliveredConfirmedAt: string|Date|null}|null|undefined} box
 * @param {string|number|null|undefined} [tadbeesaChannelId]
 * @returns {boolean}
 */
export function canRequestReturn(item, box, tadbeesaChannelId) {
  if (item == null) return false;

  const raw = item.status;
  const status = typeof raw === 'string' ? Number(raw) : raw;
  if (status !== ItemStatus.DELIVERED) return false;

  if (isPoolaLine(item)) return false;
  if (isTadbeesaLine(item, tadbeesaChannelId)) return false;
  if (hasOpenSupplierReturn(item)) return false;

  return boxAcceptsReturn(box);
}

/** i18n key for one of the six status words, or null for an unknown status —
 *  never a raw key reaching a template. */
export function returnStatusLabelKey(status) {
  switch (status) {
    case MEMBER_RETURN_STATUS.REQUEST_RECEIVED: return 'item.return.status.requestReceived';
    case MEMBER_RETURN_STATUS.ITEM_RECEIVED: return 'item.return.status.itemReceived';
    case MEMBER_RETURN_STATUS.ASKING_SUPPLIER: return 'item.return.status.askingSupplier';
    case MEMBER_RETURN_STATUS.TRYING_TO_RESELL: return 'item.return.status.tryingToResell';
    case MEMBER_RETURN_STATUS.REFUNDED: return 'item.return.status.refunded';
    case MEMBER_RETURN_STATUS.COMING_BACK: return 'item.return.status.comingBack';
    default: return null;
  }
}

/** i18n key for one of the six reason chips, or null for an unknown reason. */
export function returnReasonLabelKey(reasonKey) {
  switch (reasonKey) {
    case SUPPLIER_RETURN_REASON.WRONG_ITEM: return 'item.return.reasons.wrongItem';
    case SUPPLIER_RETURN_REASON.DAMAGED: return 'item.return.reasons.damaged';
    case SUPPLIER_RETURN_REASON.QUALITY: return 'item.return.reasons.quality';
    case SUPPLIER_RETURN_REASON.OVERAGE: return 'item.return.reasons.overage';
    case SUPPLIER_RETURN_REASON.ORDERED_BY_MISTAKE: return 'item.return.reasons.orderedByMistake';
    case SUPPLIER_RETURN_REASON.CUSTOMER_REFUSED: return 'item.return.reasons.customerRefused';
    default: return null;
  }
}

/**
 * ONE writer for the status sentence, read by both the row and the details
 * sheet, so they cannot drift apart — the same discipline `suggestionLabel()`
 * documents beside this file. '' when the status is unknown; callers gate on
 * falsiness.
 *
 * @param {ReturnType<typeof readSupplierReturn>} supplierReturn
 * @param {{t: Function}} deps
 * @returns {string}
 */
export function returnStatusLabel(supplierReturn, { t } = {}) {
  if (!supplierReturn || typeof t !== 'function') return '';
  const key = returnStatusLabelKey(supplierReturn.status);
  return key ? t(key) : '';
}

/** ONE writer for the reason chip text. '' when there is no reason to show. */
export function returnReasonLabel(supplierReturn, { t } = {}) {
  if (!supplierReturn?.reasonKey || typeof t !== 'function') return '';
  const key = returnReasonLabelKey(supplierReturn.reasonKey);
  return key ? t(key) : '';
}
